import { getCurrentLocaleCode, trans } from "@mongez/localization";
import { useLocation } from "@remix-run/react";
import { cn } from "core/shared";
import { userAtom } from "core/user";
import { URLS } from "framework/config/urls";
import { Fragment, useEffect, useState } from "react";
import headerMenuPattern from "~/assets/images/header/header-menu-pattern.png";
import { Button } from "~/design-system/components";
import { InstagramColored } from "~/design-system/components/icons";
import { BaseLink } from "~/design-system/components/link";
import { logoutPortal } from "~/modules/my-account/components";
import { headerMenuAtom } from "~/modules/shared/atoms";
import { MobileAccordionMenu } from "~/modules/shared/components/Header/components/MobileAccordionMenu";
import { buildTree } from "~/modules/shared/utils/build-tree";
import { MenuItem } from "~/shared/types";
import { headerMenuLinks } from "../data";
import { HeaderMenuItemImage } from "./HeaderMenuItemImage";

type HeaderMenuLink = (typeof headerMenuLinks)[number];

export type HeaderMenuProps = {
  documents?: MenuItem[];
};

export function HeaderMenu({ documents }: HeaderMenuProps) {
  const [hoveredLink, setHoveredLink] = useState<HeaderMenuLink | null>(null);

  const menuOpened = headerMenuAtom.useOpened();

  const { pathname, search } = useLocation();
  const currentPath = pathname + search;

  const localeCode = getCurrentLocaleCode();

  const isActive = (to?: string) => {
    if (!to) return false;
    const localizedURL = `/${localeCode}${to}`;
    return (
      currentPath === localizedURL ||
      (to !== "/" && currentPath.startsWith(`${localizedURL}/`))
    );
  };

  useEffect(() => {
    if (menuOpened) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [menuOpened]);

  if (!documents || !documents.length) return null;

  const flatterDocuments = buildTree(documents);

  return (
    <div className="relative z-1000 h-full w-full">
      <div
        className={cn(
          "bg-primary-light absolute inset-0 h-0 w-full overflow-hidden transition-all duration-300",
          menuOpened && "h-[calc(100dvh-var(--header-height))] bg-white",
        )}
        style={{
          backgroundImage: `url(${headerMenuPattern})`,
        }}
      >
        <div className="relative container flex h-[calc(100dvh-165px)] shrink-0 flex-col items-center gap-4 overflow-y-auto bg-white/50 pt-2 lg:gap-6 lg:pt-6">
          <ul className="relative flex w-full flex-col gap-4">
            <HeaderMenuItemImage hoveredLink={hoveredLink} />
            {flatterDocuments.map((menuItem) => {
              return (
                <Fragment key={menuItem.id}>
                  {menuItem.children ? (
                    <MobileAccordionMenu menuItem={menuItem} />
                  ) : (
                    <li>
                      <div className="font-jost flex w-full justify-between gap-1 transition-all hover:text-[#FF506C]">
                        {menuItem.link && !menuItem.category && (
                          <BaseLink
                            onClick={headerMenuAtom.close}
                            to={menuItem.link as string}
                            className={cn(
                              "text-2xl font-semibold",
                              isActive(menuItem.link as string) &&
                                "text-primary-default",
                            )}
                          >
                            {trans(menuItem.name)}
                          </BaseLink>
                        )}
                        {menuItem.category && (
                          <BaseLink
                            onClick={headerMenuAtom.close}
                            to={URLS.shop.viewCategory(menuItem.category)}
                            className={cn(
                              "text-2xl font-semibold",
                              isActive(
                                URLS.shop.viewCategory(menuItem.category),
                              ) && "text-primary-default",
                            )}
                          >
                            {trans(menuItem.name)}
                          </BaseLink>
                        )}
                      </div>
                    </li>
                  )}
                </Fragment>
              );
            })}
          </ul>
          <a
            href="https://www.instagram.com/nannis_cakes"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden lg:block"
            onClick={headerMenuAtom.close}
          >
            <InstagramColored className="size-10 2xl:size-16" />
          </a>
          <UserLinks />
        </div>
      </div>
    </div>
  );
}

function UserLinks() {
  const user = userAtom.useValue();

  if (user.userType !== "user") {
    return (
      <ul className="border-border font-jost relative flex h-fit w-full shrink-0 flex-col gap-3 overflow-y-auto border-t py-4 text-2xl font-semibold lg:hidden lg:gap-6 lg:py-6">
        <UserLink to={URLS.auth.login} label="login" />
        <UserLink to={URLS.auth.register} label="register" />
        <UserLink to={URLS.cart} label="cart.title" />
      </ul>
    );
  }

  return (
    <ul className="border-border relative flex h-fit shrink-0 flex-col items-center gap-3 overflow-y-auto border-t py-4 lg:hidden lg:gap-6 lg:py-6">
      <UserLink to={URLS.account.root} label="myAccount" />
      <UserLink to={URLS.account.orders} label="myOrders" />
      <UserLink to={URLS.cart} label="cart.title" />
      <Button
        onClick={() => {
          logoutPortal.open();
          headerMenuAtom.close();
        }}
        className="hover:bg-primary-light justify-center rounded-full border px-6 py-2 text-xl font-medium capitalize"
      >
        {trans("logout")}
      </Button>
    </ul>
  );
}

type UserLinkProps = {
  to: string;
  label: string;
};

function UserLink({ to, label }: UserLinkProps) {
  const { pathname } = useLocation();
  const localeCode = getCurrentLocaleCode();
  const localizedURL = `/${localeCode}${to}`;
  const isActive = pathname === localizedURL;

  return (
    <li>
      <BaseLink
        to={to}
        onClick={headerMenuAtom.close}
        className={cn(
          "text-center text-2xl text-black duration-300 hover:text-[#FF506C] lg:text-3xl",
          isActive && "text-primary-default",
        )}
      >
        {trans(label)}
      </BaseLink>
    </li>
  );
}
